import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Squelette } from '../components/ui';
import { centimes, montant } from '../lib/format';

interface Params {
  raisonSociale: string; rc: string; telephone: string; adresse: string; email: string; siteWeb: string;
  deviseReference: string; tamponPreparationMin: number;
  ageMinimum: number; ancienneteMinimumPermis: number; rayonKm: number; wilayasAutorisees: string[];
  heureOuverture: string; heureFermeture: string;
  hauteSaisonDebut: string; hauteSaisonFin: string;
  conditionsGeneralesFr: { titre: string; texte: string }[];
  conditionsGeneralesAr: { titre: string; texte: string }[];
  mentionGeolocalisationFr: string; mentionGeolocalisationAr: string;
}
interface Supp { _id: string; code: string; libelle: string; libelleAr: string; unite: string; prixMinor: number; moment: string; automatique: boolean; articleContrat?: string; actif: boolean }

export default function Parametres() {
  const { peut } = useAuth();
  const modifiable = peut('gerer.parametres');
  const [p, setP] = useState<Params | null>(null);
  const [supp, setSupp] = useState<Supp[]>([]);
  const [onglet, setOnglet] = useState<'agence' | 'regles' | 'supplements' | 'cgl'>('agence');
  const [msg, setMsg] = useState('');
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => {
    api.get<Params>('/parametres').then(setP);
    api.get<Supp[]>('/supplements').then(setSupp);
  }, []);
  useEffect(charger, [charger]);
  if (!p) return <Squelette lignes={8} />;

  async function sauver(patch: Partial<Params>) {
    setErreur(''); setMsg('');
    try { setP(await api.patch<Params>('/parametres', patch)); setMsg('Enregistré.'); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }
  async function sauverSupp(s: Supp, prix: string) {
    setErreur('');
    try { await api.patch(`/supplements/${s._id}`, { prixMinor: centimes(Number(prix || 0)) }); setMsg(`« ${s.libelle} » mis à jour.`); charger(); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  const arManquant = p.conditionsGeneralesAr.filter((a) => a.texte.startsWith('[')).length;
  const suppSansPrix = supp.filter((s) => s.prixMinor === 0 && s.code !== 'dommage');

  return (
    <>
      <div className="entete">
        <div className="titres"><h1>Paramètres</h1><p>{modifiable ? 'Réservé a Hichem' : 'Lecture seule'}</p></div>
        <div className="actions">
          {(['agence', 'regles', 'supplements', 'cgl'] as const).map((o) => (
            <button key={o} className={`btn btn-s ${onglet === o ? 'btn-p' : ''}`} onClick={() => setOnglet(o)}>
              {{ agence: 'Agence', regles: 'Règles du contrat', supplements: 'Suppléments', cgl: 'Conditions générales' }[o]}
            </button>
          ))}
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      {onglet !== 'cgl' && arManquant > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={`${arManquant} article(s) sans texte arabe`}>
            Le contrat imprime sort déjà en deux colonnes, mais les corps de texte arabes doivent être colles depuis
            votre document existant. <button className="btn btn-s" style={{ marginLeft: 8 }} onClick={() => setOnglet('cgl')}>Les compléter</button>
          </Bandeau>
        </div>
      )}

      {onglet === 'agence' && (
        <Carte titre="Identité imprimee sur vos documents">
          <div className="grille g2">
            {([
              ['raisonSociale', 'Raison sociale'], ['rc', 'Registre de commerce'],
              ['telephone', 'Téléphone'], ['email', 'E-mail'],
              ['adresse', 'Adresse'], ['siteWeb', 'Site web'],
            ] as const).map(([k, l]) => (
              <Champ key={k} label={l}>
                <input value={p[k]} readOnly={!modifiable} onChange={(e) => setP({ ...p, [k]: e.target.value })} />
              </Champ>
            ))}
          </div>
          {modifiable && (
            <button className="btn btn-p" style={{ marginTop: 14 }} onClick={() => void sauver({
              raisonSociale: p.raisonSociale, rc: p.rc, telephone: p.telephone, email: p.email, adresse: p.adresse, siteWeb: p.siteWeb,
            })}>Enregistrer</button>
          )}
        </Carte>
      )}

      {onglet === 'regles' && (
        <div className="grille g2" style={{ alignItems: 'start' }}>
          <Carte titre="Article 01 — age et qualification">
            <div className="grille g2">
              <Champ label="Age minimum" aide="Bloque la réservation en dessous.">
                <input type="number" value={p.ageMinimum} readOnly={!modifiable} onChange={(e) => setP({ ...p, ageMinimum: Number(e.target.value) })} />
              </Champ>
              <Champ label="Ancienneté de permis (ans)">
                <input type="number" value={p.ancienneteMinimumPermis} readOnly={!modifiable} onChange={(e) => setP({ ...p, ancienneteMinimumPermis: Number(e.target.value) })} />
              </Champ>
              <Champ label="Rayon autorisé (km)">
                <input type="number" value={p.rayonKm} readOnly={!modifiable} onChange={(e) => setP({ ...p, rayonKm: Number(e.target.value) })} />
              </Champ>
              <Champ label="Wilayas autorisées">
                <input value={p.wilayasAutorisees.join(', ')} readOnly={!modifiable}
                       onChange={(e) => setP({ ...p, wilayasAutorisees: e.target.value.split(',').map((x) => x.trim()) })} />
              </Champ>
            </div>
          </Carte>

          <Carte titre="Article 02 — horaires, et saisons">
            <div className="grille g2">
              <Champ label="Ouverture"><input type="time" value={p.heureOuverture} readOnly={!modifiable} onChange={(e) => setP({ ...p, heureOuverture: e.target.value })} /></Champ>
              <Champ label="Fermeture"><input type="time" value={p.heureFermeture} readOnly={!modifiable} onChange={(e) => setP({ ...p, heureFermeture: e.target.value })} /></Champ>
              <Champ label="Haute saison — début (MM-JJ)" aide="L'ete. Le tarif haute saison de chaque voiture s'applique.">
                <input value={p.hauteSaisonDebut} readOnly={!modifiable} onChange={(e) => setP({ ...p, hauteSaisonDebut: e.target.value })} placeholder="06-01" />
              </Champ>
              <Champ label="Haute saison — fin (MM-JJ)">
                <input value={p.hauteSaisonFin} readOnly={!modifiable} onChange={(e) => setP({ ...p, hauteSaisonFin: e.target.value })} placeholder="09-30" />
              </Champ>
              <Champ label="Temps de preparation (min)" aide="Une voiture qui rentre à 11 h n'est pas libre à 11 h.">
                <input type="number" value={p.tamponPreparationMin} readOnly={!modifiable} onChange={(e) => setP({ ...p, tamponPreparationMin: Number(e.target.value) })} />
              </Champ>
            </div>
          </Carte>

          {modifiable && (
            <button className="btn btn-p btn-l" onClick={() => void sauver({
              ageMinimum: p.ageMinimum, ancienneteMinimumPermis: p.ancienneteMinimumPermis,
              rayonKm: p.rayonKm, wilayasAutorisees: p.wilayasAutorisees,
              heureOuverture: p.heureOuverture, heureFermeture: p.heureFermeture,
              hauteSaisonDebut: p.hauteSaisonDebut, hauteSaisonFin: p.hauteSaisonFin,
              tamponPreparationMin: p.tamponPreparationMin,
            })}>Enregistrer les règles</button>
          )}
        </div>
      )}

      {onglet === 'supplements' && (
        <>
          {suppSansPrix.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <Bandeau type="warn" titre={`${suppSansPrix.length} supplement(s) sans prix`}>
                {suppSansPrix.map((s) => s.libelle).join(', ')} — ces prix ne figurent pas dans votre contrat,
                il faut les saisir ici pour qu'ils soient factures.
              </Bandeau>
            </div>
          )}
          <Carte titre="Catalogue unique — le siège bébé et le retard vivent dans la même liste" pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl">
                <thead><tr><th>Supplément</th><th>Arabe</th><th>Unité</th><th>Moment</th><th>Article</th><th className="droite">Prix</th><th /></tr></thead>
                <tbody>
                  {supp.map((s) => <LigneSupp key={s._id} s={s} modifiable={peut('gerer.supplements')} onSauver={sauverSupp} />)}
                </tbody>
              </table>
            </div>
          </Carte>
          <p className="mini sourdine" style={{ marginTop: 12 }}>
            Les suppléments marqués « auto » sont proposés tout seuls au retour, avec leur formule.
            Modifier un prix ici ne change jamais un contrat déjà etabli.
          </p>
        </>
      )}

      {onglet === 'cgl' && (
        <>
          <div style={{ marginBottom: 14 }}>
            <Bandeau type="info" titre="Ces textes sont imprimés au verso du contrat">
              Colonne gauche en francais, colonne droite en arabe. Les titres arabes sont deja saisis ;
              <b> les corps de texte doivent être colles depuis votre contrat existant</b> — nous n'ecrivons pas
              de texte juridique arabe a votre place.
            </Bandeau>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            {p.conditionsGeneralesFr.map((a, i) => (
              <Carte key={i} titre={a.titre}>
                <div className="grille g2">
                  <Champ label="Français">
                    <textarea style={{ minHeight: 130 }} value={a.texte} readOnly={!modifiable}
                              onChange={(e) => { const c = [...p.conditionsGeneralesFr]; c[i] = { ...a, texte: e.target.value }; setP({ ...p, conditionsGeneralesFr: c }); }} />
                  </Champ>
                  <Champ label={p.conditionsGeneralesAr[i]?.titre ?? 'Arabe'}
                         erreur={p.conditionsGeneralesAr[i]?.texte.startsWith('[') ? 'Texte arabe manquant — à coller depuis votre contrat' : undefined}>
                    <textarea dir="rtl" style={{ minHeight: 130, fontFamily: '"Noto Sans Arabic",Tahoma,sans-serif' }}
                              value={p.conditionsGeneralesAr[i]?.texte ?? ''} readOnly={!modifiable}
                              onChange={(e) => { const c = [...p.conditionsGeneralesAr]; c[i] = { titre: c[i]?.titre ?? '', texte: e.target.value }; setP({ ...p, conditionsGeneralesAr: c }); }} />
                  </Champ>
                </div>
              </Carte>
            ))}
            <Carte titre="Mention de géolocalisation">
              <div className="grille g2">
                <Champ label="Français">
                  <textarea value={p.mentionGeolocalisationFr} readOnly={!modifiable} onChange={(e) => setP({ ...p, mentionGeolocalisationFr: e.target.value })} />
                </Champ>
                <Champ label="Arabe" erreur={p.mentionGeolocalisationAr.startsWith('[') ? 'A coller depuis votre contrat' : undefined}>
                  <textarea dir="rtl" style={{ fontFamily: '"Noto Sans Arabic",Tahoma,sans-serif' }}
                            value={p.mentionGeolocalisationAr} readOnly={!modifiable} onChange={(e) => setP({ ...p, mentionGeolocalisationAr: e.target.value })} />
                </Champ>
              </div>
            </Carte>
            {modifiable && (
              <button className="btn btn-p btn-l" onClick={() => void sauver({
                conditionsGeneralesFr: p.conditionsGeneralesFr, conditionsGeneralesAr: p.conditionsGeneralesAr,
                mentionGeolocalisationFr: p.mentionGeolocalisationFr, mentionGeolocalisationAr: p.mentionGeolocalisationAr,
              })}>Enregistrer les conditions générales</button>
            )}
          </div>
        </>
      )}
    </>
  );
}

function LigneSupp({ s, modifiable, onSauver }: { s: Supp; modifiable: boolean; onSauver: (s: Supp, prix: string) => void }) {
  const [prix, setPrix] = useState(String(s.prixMinor / 100));
  const change = prix !== String(s.prixMinor / 100);
  return (
    <tr>
      <td><b>{s.libelle}</b> {s.automatique && <span className="badge en_cours" style={{ marginLeft: 4 }}>auto</span>}</td>
      <td dir="rtl" style={{ fontFamily: '"Noto Sans Arabic",Tahoma,sans-serif' }}>{s.libelleAr || '—'}</td>
      <td className="mini sourdine">par {s.unite}</td>
      <td className="mini sourdine">{s.moment}</td>
      <td className="mini sourdine">{s.articleContrat ? `art. ${s.articleContrat}` : '—'}</td>
      <td className="droite" style={{ width: 170 }}>
        {modifiable
          ? <input type="number" value={prix} onChange={(e) => setPrix(e.target.value)}
                   style={{ width: 120, textAlign: 'right', height: 32, border: '1px solid var(--bord-fort)', borderRadius: 8, padding: '0 8px' }} />
          : <span className="montant">{montant(s.prixMinor)}</span>}
      </td>
      <td className="droite">{modifiable && change && <button className="btn btn-s btn-p" onClick={() => onSauver(s, prix)}>Enregistrer</button>}</td>
    </tr>
  );
}
